/**
 * AppliPlusSection — "Appli +" sidebar section.
 * Shows pinned module pills + expandable catalog with search + placement picker.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Star, X, Lock, ChevronDown, Search, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { usePinnedModules, type PinPlacement } from "@/hooks/usePinnedModules";
import { MODULES_CATALOG } from "@/constants/modulesCatalog";
import { useModuleUnlock } from "@/hooks/useModuleUnlock";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const COMING_SOON_IDS = new Set(["avatar", "challenges"]);

/** Value-prop tooltips for coming-soon / preview modules */
const COMING_SOON_TOOLTIPS: Record<string, { title: string; features: [string, string]; availability: string }> = {
  wellness: {
    title: "Gère ton alimentation en lien avec tes objectifs sportifs.",
    features: [
      "Plans nutritionnels générés par le Cerveau",
      "Suivi habitudes & récupération intégré",
    ],
    availability: "Disponible — module actif 🟢",
  },
  avatar: {
    title: "Visualise ta progression physique via un avatar 3D.",
    features: [
      "Évolution visuelle en temps réel",
      "Projection basée sur tes performances",
    ],
    availability: "Prévu — 2026",
  },
  challenges: {
    title: "Participe à des défis communautaires et grimpe au classement.",
    features: [
      "Défis temporaires avec objectifs précis",
      "Récompenses en mérites pour les participants",
    ],
    availability: "Prévu — 2026",
  },
};

/** Renders rich tooltip content for a module with value-prop info */
function ModuleTooltipContent({ moduleId }: { moduleId: string }) {
  const tip = COMING_SOON_TOOLTIPS[moduleId];
  if (!tip) return null;
  return (
    <div className="space-y-1.5 max-w-[220px]">
      <p className="text-xs font-medium text-foreground">{tip.title}</p>
      <ul className="text-[11px] text-muted-foreground space-y-0.5">
        {tip.features.map((f, i) => (
          <li key={i}>• {f}</li>
        ))}
      </ul>
      <p className="text-[10px] font-medium text-primary">{tip.availability}</p>
    </div>
  );
}
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from "@/components/ui/sidebar";

const PLACEMENT_OPTIONS: { value: PinPlacement; label: string }[] = [
  { value: "sidebar", label: "Sidebar" },
  { value: "header", label: "En haut" },
  { value: "both", label: "Les deux" },
];

function PlacementPicker({
  current,
  onChange,
  onOpenModules,
}: {
  current: PinPlacement;
  onChange: (p: PinPlacement) => void;
  onOpenModules: () => void;
}) {
  return (
    <div className="px-2 py-2 space-y-1.5">
      <p className="text-[11px] text-muted-foreground font-medium">
        📍 Où afficher tes favoris ?
      </p>
      <div className="flex gap-1">
        {PLACEMENT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex-1 text-[11px] font-medium rounded-md px-2 min-h-[36px] transition-colors",
              current === opt.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <button
        onClick={onOpenModules}
        className="w-full flex items-center gap-2 text-[11px] font-medium rounded-md px-2 min-h-[36px] bg-muted text-foreground hover:bg-muted/80 transition-colors"
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        <span>Voir tous les modules</span>
      </button>
    </div>
  );
}

export function AppliPlusSection() {
  const navigate = useNavigate();
  const {
    pinnedIds,
    placement,
    setPlacement,
    pin,
    unpin,
    isPinned,
    pinnedModules,
  } = usePinnedModules();
  const { moduleStates } = useModuleUnlock();

  const [expanded, setExpanded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  const showSidebarPills = placement === "sidebar" || placement === "both";

  const searchLower = search.toLowerCase().trim();
  const filteredModules = useMemo(() => {
    const list = MODULES_CATALOG.filter(
      (m) =>
        m.section !== "future" &&
        m.id !== "library" &&
        m.id !== "community" &&
        m.id !== "achievements" &&
        m.id !== "billing"
    );
    if (!searchLower) return list;
    return list.filter(
      (m) =>
        m.name.toLowerCase().includes(searchLower) ||
        m.tags.some((t) => t.includes(searchLower))
    );
  }, [searchLower]);

  const isModuleLocked = (moduleId: string) => {
    const state = moduleStates[moduleId];
    return state?.status === "locked" || state?.status === "coming_soon";
  };

  return (
    <SidebarGroup>
      {/* Pinned pills — above header */}
      {showSidebarPills && pinnedModules.length > 0 && (
        <div className="px-2 pb-1 space-y-0.5">
          {pinnedModules.map((mod) => {
            const locked = isModuleLocked(mod.id);
            const comingSoon = COMING_SOON_IDS.has(mod.id);
            const hasTooltip = mod.id in COMING_SOON_TOOLTIPS && (comingSoon || locked);
            const inner = (
              <div
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2 group overflow-hidden",
                  locked
                    ? "opacity-50 cursor-not-allowed"
                    : comingSoon
                    ? "opacity-60 cursor-pointer hover:bg-sidebar-accent"
                    : "hover:bg-sidebar-accent cursor-pointer"
                )}
                onClick={() => {
                  if (locked) return;
                  if (comingSoon) {
                    toast(COMING_SOON_TOOLTIPS[mod.id]?.title ?? "Ce module arrive bientôt");
                    return;
                  }
                  navigate(mod.route);
                }}
                role="button"
                tabIndex={0}
              >
                <span className="text-sm shrink-0">{mod.emoji}</span>
                <span className="text-xs truncate flex-1 min-w-0 py-2" title={mod.name}>
                  {mod.name}
                </span>
                {comingSoon && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-white shrink-0">
                    Bientôt
                  </span>
                )}
                {!locked && !comingSoon && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      unpin(mod.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 min-h-[32px] min-w-[32px] flex items-center justify-center shrink-0"
                    aria-label={`Désépingler ${mod.name}`}
                  >
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            );
            return hasTooltip ? (
              <Tooltip key={mod.id} delayDuration={300}>
                <TooltipTrigger asChild>{inner}</TooltipTrigger>
                <TooltipContent side="right" className="p-3">
                  <ModuleTooltipContent moduleId={mod.id} />
                </TooltipContent>
              </Tooltip>
            ) : (
              <div key={mod.id}>{inner}</div>
            );
          })}
        </div>
      )}

      {/* Header */}
      <SidebarGroupLabel className="text-xs uppercase tracking-wider flex items-center justify-between pr-2">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 min-h-[44px]"
        >
          <span>⊞ Appli +</span>
          {pinnedIds.length > 0 && (
            <span className="text-[10px] text-muted-foreground">
              · {pinnedIds.length}
            </span>
          )}
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform text-muted-foreground",
              expanded && "rotate-180"
            )}
          />
        </button>
        <button
          onClick={() => setShowPicker((v) => !v)}
          className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Choisir l'emplacement des favoris"
          title="Choisir l'emplacement des favoris"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
      </SidebarGroupLabel>

      {/* Placement picker */}
      {showPicker && (
        <PlacementPicker
          current={placement}
          onChange={setPlacement}
          onOpenModules={() => {
            setShowPicker(false);
            navigate("/modules");
          }}
        />
      )}

      {/* Expanded module list */}
      {expanded && (
        <SidebarGroupContent>
          <div className="px-2 pb-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer les modules…"
                className="h-7 pl-7 text-xs bg-sidebar-accent/50 border-sidebar-border"
              />
            </div>
          </div>
          <div className="max-h-[280px] overflow-y-auto px-1 space-y-0.5">
            {filteredModules.map((mod) => {
              const locked = isModuleLocked(mod.id);
              const pinned = isPinned(mod.id);
              const comingSoon = COMING_SOON_IDS.has(mod.id);
              const hasTooltip = mod.id in COMING_SOON_TOOLTIPS && (comingSoon || locked);
              const inner = (
                <div
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-2 overflow-hidden",
                    locked
                      ? "opacity-50"
                      : comingSoon
                      ? "opacity-60 cursor-pointer hover:bg-sidebar-accent"
                      : "hover:bg-sidebar-accent cursor-pointer"
                  )}
                  onClick={() => {
                    if (locked) return;
                    if (comingSoon) {
                      toast(COMING_SOON_TOOLTIPS[mod.id]?.title ?? "Ce module arrive bientôt");
                      return;
                    }
                    navigate(mod.route);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="text-sm shrink-0">{mod.emoji}</span>
                  <span className="text-xs truncate flex-1 min-w-0 py-2" title={mod.name}>
                    {mod.name}
                  </span>
                  {comingSoon ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-white shrink-0">
                      Bientôt
                    </span>
                  ) : locked ? (
                    <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        pinned ? unpin(mod.id) : pin(mod.id);
                      }}
                      className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center shrink-0"
                      aria-label={
                        pinned
                          ? `Désépingler ${mod.name}`
                          : `Épingler ${mod.name}`
                      }
                    >
                      <Star
                        className={cn(
                          "h-3.5 w-3.5 transition-colors",
                          pinned
                            ? "text-primary fill-primary"
                            : "text-muted-foreground"
                        )}
                      />
                    </button>
                  )}
                </div>
              );
              return hasTooltip ? (
                <Tooltip key={mod.id} delayDuration={300}>
                  <TooltipTrigger asChild>{inner}</TooltipTrigger>
                  <TooltipContent side="right" className="p-3">
                    <ModuleTooltipContent moduleId={mod.id} />
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div key={mod.id}>{inner}</div>
              );
            })}
            {filteredModules.length === 0 && (
              <p className="text-xs text-muted-foreground px-2 py-2">
                Aucun module trouvé
              </p>
            )}
          </div>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}
